/* =========================================================
   OVERTOP — Image Preload v2.0.0
   ---------------------------------------------------------
   Product-page only helper for fast variant image switches.

   - Uses shared OT_PRODUCT_DATA_CACHE from the bundle resolver
   - Preloads the current variant featured image at high priority
   - Idle-prefetches only featured images for the other variants
   - On hover/touch/pointer intent, preloads that variant's range
   - Exposes OT_IMAGE_PRELOAD_VARIANT(variantId, priority)
   ========================================================= */

(function () {
  'use strict';

  var VERSION = '2.0.0';
  var WIDTH = 832;
  var preloadedUrls = {};
  var productData = null;
  var mediaIdToSrc = {};
  var rangesByVariant = {};
  var featuredByVariant = {};
  var setupDone = false;
  var loadPromise = null;

  window.__OT_IMAGE_PRELOAD_VERSION = VERSION;

  function getHandle() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function rootUrl() {
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return String(window.Shopify.routes.root || '/').replace(/\/$/, '');
      }
    } catch (err) {}
    return '';
  }

  function getProductDataCache() {
    if (window.OT_PRODUCT_DATA_CACHE && window.OT_PRODUCT_DATA_CACHE.fetchByHandle) {
      return window.OT_PRODUCT_DATA_CACHE;
    }

    var cache = {
      products: {},
      promises: {},
      fetchByHandle: function (handle) {
        handle = String(handle || '').trim();
        if (!handle) return Promise.resolve(null);
        if (cache.products[handle]) return Promise.resolve(cache.products[handle]);
        if (cache.promises[handle]) return cache.promises[handle];

        cache.promises[handle] = fetch(rootUrl() + '/products/' + encodeURIComponent(handle) + '.js', {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        }).then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        }).then(function (product) {
          cache.products[handle] = product;
          if (product && product.handle) cache.products[product.handle] = product;
          return product;
        }).catch(function (err) {
          delete cache.promises[handle];
          throw err;
        });

        return cache.promises[handle];
      },
      getByHandle: function (handle) {
        return cache.products[String(handle || '').trim()] || null;
      }
    };

    window.OT_PRODUCT_DATA_CACHE = cache;
    return cache;
  }

  function idle(fn, timeout) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(fn, { timeout: timeout || 1500 });
    } else {
      setTimeout(fn, 450);
    }
  }

  function preconnect(url) {
    if (document.querySelector('link[rel="preconnect"][href="' + url + '"]')) return;
    var link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = url;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  function normalizeSrc(src) {
    if (!src) return null;
    src = String(src);
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src;
  }

  function buildSizedUrl(src, width) {
    src = normalizeSrc(src);
    if (!src) return null;
    if (/([?&])width=/.test(src)) return src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + (width || WIDTH);
  }

  function preloadImage(src, priority) {
    var url = buildSizedUrl(src, WIDTH);
    if (!url || preloadedUrls[url]) return;
    preloadedUrls[url] = true;

    var link = document.createElement('link');
    link.rel = priority === 'high' ? 'preload' : 'prefetch';
    link.as = 'image';
    link.href = url;
    if (priority === 'high') {
      try { link.fetchPriority = 'high'; } catch (err) {}
    }
    document.head.appendChild(link);
  }

  function variantFeaturedMediaId(variant) {
    return String(
      (variant && variant.featured_media && variant.featured_media.id) ||
      (variant && variant.featured_image && variant.featured_image.id) ||
      ''
    );
  }

  function buildIndexes(product) {
    var mediaIdToPosition = {};
    mediaIdToSrc = {};
    rangesByVariant = {};
    featuredByVariant = {};

    if (!product || !product.media || !product.variants) return;

    product.media.forEach(function (media) {
      var id = String(media.id || '');
      if (!id) return;
      mediaIdToPosition[id] = media.position;
      mediaIdToSrc[id] = media.src || (media.preview_image && media.preview_image.src) || null;
    });

    var variantsWithFeatured = product.variants.map(function (variant) {
      var fmId = variantFeaturedMediaId(variant);
      return {
        id: String(variant.id || ''),
        fmId: fmId,
        featPos: mediaIdToPosition[fmId]
      };
    }).filter(function (variant) {
      return variant.id && typeof variant.featPos === 'number';
    }).sort(function (a, b) {
      return a.featPos - b.featPos;
    });

    variantsWithFeatured.forEach(function (variant, index) {
      var startPos = variant.featPos;
      var endPos = index + 1 < variantsWithFeatured.length
        ? variantsWithFeatured[index + 1].featPos - 1
        : Infinity;
      var srcs = [];

      product.media.forEach(function (media) {
        if (media.position >= startPos && media.position <= endPos) {
          var src = media.src || (media.preview_image && media.preview_image.src);
          if (src) srcs.push(src);
        }
      });

      rangesByVariant[variant.id] = srcs;
      featuredByVariant[variant.id] = mediaIdToSrc[variant.fmId] || srcs[0] || null;
    });
  }

  function getCurrentVariantId() {
    var q = new URL(window.location.href).searchParams.get('variant');
    if (q) return String(q);

    var checked = document.querySelector('variant-picker input:checked[data-variant-id], variant-picker option:checked[data-variant-id]');
    if (checked && checked.dataset && checked.dataset.variantId) return String(checked.dataset.variantId);

    if (!productData || !productData.variants || !productData.variants.length) return null;
    var available = productData.variants.find(function (variant) { return variant.available; });
    return String((available || productData.variants[0]).id || '');
  }

  function loadProduct() {
    if (loadPromise) return loadPromise;
    var handle = getHandle();
    if (!handle) return Promise.resolve(null);

    loadPromise = getProductDataCache().fetchByHandle(handle).then(function (product) {
      productData = product;
      buildIndexes(product);
      return product;
    }).catch(function () {
      return null;
    });

    return loadPromise;
  }

  function preloadVariant(variantId, priority) {
    variantId = String(variantId || '');
    if (!variantId) return;

    loadProduct().then(function () {
      // the gallery's own answer to "which images are this colour's" (ot-variant-media-filter), else the old range
      var ids = window.OT_MEDIA_OF_VARIANT && window.OT_MEDIA_OF_VARIANT(variantId);
      var srcs = ids ? ids.map(function (id) { return mediaIdToSrc[id]; }).filter(Boolean) : (rangesByVariant[variantId] || []);
      var featured = featuredByVariant[variantId];

      if (featured) preloadImage(featured, priority === 'high' ? 'high' : 'low');

      if (priority === 'high') {
        srcs.slice(0, 3).forEach(function (src) { preloadImage(src, 'high'); });
        idle(function () {
          srcs.slice(3).forEach(function (src) { preloadImage(src, 'low'); });
        }, 1000);
      } else {
        srcs.forEach(function (src) { preloadImage(src, 'low'); });
      }
    });
  }

  function getVariantIdFromIntentTarget(target) {
    if (!target || !target.closest) return null;

    var direct = target.closest('[data-variant-id]');
    if (direct && direct.dataset && direct.dataset.variantId) return direct.dataset.variantId;

    var label = target.closest('label, .variant-option__button-label');
    if (label) {
      var input = label.querySelector('[data-variant-id]');
      if (input && input.dataset && input.dataset.variantId) return input.dataset.variantId;
    }

    var select = target.closest('select');
    if (select && select.options && select.selectedIndex >= 0) {
      var selected = select.options[select.selectedIndex];
      if (selected && selected.dataset && selected.dataset.variantId) return selected.dataset.variantId;
    }

    return null;
  }

  function setupIntentPreload() {
    if (setupDone) return;
    setupDone = true;

    function handler(event) {
      var variantId = getVariantIdFromIntentTarget(event.target);
      if (!variantId) return;
      var high = event.type === 'pointerdown' || event.type === 'touchstart' || event.type === 'change';
      preloadVariant(variantId, high ? 'high' : 'low');
    }

    document.addEventListener('pointerdown', handler, { passive: true, capture: true });
    document.addEventListener('touchstart', handler, { passive: true, capture: true });
    document.addEventListener('mouseover', handler, { passive: true, capture: true });
    document.addEventListener('focusin', handler, { passive: true, capture: true });
    document.addEventListener('change', handler, { passive: true, capture: true });
  }

  function boot() {
    preconnect('https://cdn.shopify.com');
    if (!getHandle()) return;

    setupIntentPreload();

    loadProduct().then(function () {
      var currentId = getCurrentVariantId();
      if (currentId) preloadVariant(currentId, 'high');

      idle(function () {
        Object.keys(featuredByVariant).forEach(function (variantId) {
          if (variantId !== String(currentId || '') && featuredByVariant[variantId]) {
            preloadImage(featuredByVariant[variantId], 'low');
          }
        });
      }, 1200);
    });
  }

  window.OT_IMAGE_PRELOAD_VARIANT = preloadVariant;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
